'use client'

import type { Form as FormType } from '@payloadcms/plugin-form-builder/types'

import { useRouter } from 'next/navigation'
import type React from 'react'
import { useCallback, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { getClientSideURL } from '@/utilities/getURL'
import { getRumSessionId, setTelemetryUser } from '@/utilities/telemetry'

type FormField = {
  blockType?: string
  name?: string
  label?: string | null
  placeholder?: string | null
  required?: boolean | null
  width?: number | null
  options?: { label: string; value: string }[] | null
}

const widthStyle = (w?: number | null): React.CSSProperties =>
  !w || w >= 100 ? { width: '100%' } : { width: `calc(${w}% - 0.625rem)` }

export const ContactForm: React.FC<{ form: FormType }> = ({ form }) => {
  const { id: formID, confirmationType, redirect, submitButtonLabel } = form
  const formFields = (form.fields ?? []) as FormField[]

  const { control, register, handleSubmit } = useForm()
  const [isLoading, setIsLoading] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const [error, setError] = useState<string | undefined>()
  const router = useRouter()

  const onSubmit = useCallback(
    (data: Record<string, unknown>) => {
      const submit = async () => {
        setError(undefined)
        setIsLoading(true)
        const dataToSend = Object.entries(data).map(([name, value]) => ({
          field: name,
          value,
        }))

        // Associate the RUM session with the submitter so the journey is
        // attributable, and forward the session id so the backend span can be
        // correlated back to this session.
        const email = dataToSend.find((d) => /e?mail/i.test(d.field))?.value
        const name = dataToSend.find((d) => /name|imi[eę]/i.test(d.field))?.value
        if (typeof email === 'string') {
          setTelemetryUser({ id: email, email, name: typeof name === 'string' ? name : undefined })
        }
        const sessionId = getRumSessionId()

        try {
          const req = await fetch(`${getClientSideURL()}/api/form-submissions`, {
            body: JSON.stringify({
              form: formID,
              submissionData: dataToSend,
            }),
            headers: {
              'Content-Type': 'application/json',
              ...(sessionId ? { 'x-rum-session-id': sessionId } : {}),
            },
            method: 'POST',
          })
          const res = await req.json()
          setIsLoading(false)
          if (req.status >= 400) {
            setError(res.errors?.[0]?.message || 'Wystąpił błąd. Spróbuj ponownie.')
            return
          }
          setHasSubmitted(true)
          if (confirmationType === 'redirect' && redirect?.url) router.push(redirect.url)
        } catch {
          setIsLoading(false)
          setError('Coś poszło nie tak.')
        }
      }
      void submit()
    },
    [formID, confirmationType, redirect, router],
  )

  if (hasSubmitted && confirmationType !== 'redirect') {
    return (
      <div className="bg-green-50 border border-green-200 text-green-800 rounded-xl p-6 font-medium">
        Dziękujemy! Wiadomość została wysłana.
      </div>
    )
  }

  return (
    <form className="flex flex-wrap gap-5" onSubmit={handleSubmit(onSubmit)}>
      {formFields.map((field, i) => {
        const id = `contact-${field.name ?? i}`
        const required = Boolean(field.required)

        if (field.blockType === 'message') return null

        if (field.blockType === 'textarea') {
          return (
            <div key={i} style={widthStyle(field.width)} className="space-y-2">
              {field.label && <Label htmlFor={id}>{field.label}</Label>}
              <Textarea
                id={id}
                rows={8}
                placeholder={field.placeholder ?? ''}
                {...register(field.name ?? id, { required })}
              />
            </div>
          )
        }

        if (field.blockType === 'select') {
          return (
            <div key={i} style={widthStyle(field.width)} className="space-y-2">
              {field.label && <Label htmlFor={id}>{field.label}</Label>}
              <Controller
                control={control}
                name={field.name ?? id}
                rules={{ required }}
                render={({ field: ctl }) => (
                  <Select onValueChange={ctl.onChange} value={ctl.value}>
                    <SelectTrigger id={id}>
                      <SelectValue placeholder={field.placeholder ?? 'Wybierz...'} />
                    </SelectTrigger>
                    <SelectContent>
                      {(field.options ?? []).map((opt, oi) => (
                        <SelectItem key={oi} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          )
        }

        // text, email, number, and any other text-like field
        const inputType =
          field.blockType === 'email' ? 'email' : field.blockType === 'number' ? 'number' : 'text'
        return (
          <div key={i} style={widthStyle(field.width)} className="space-y-2">
            {field.label && <Label htmlFor={id}>{field.label}</Label>}
            <Input
              id={id}
              type={inputType}
              placeholder={field.placeholder ?? ''}
              {...register(field.name ?? id, { required })}
            />
          </div>
        )
      })}

      {error && <p className="w-full text-sm text-red-600">{error}</p>}

      <Button
        type="submit"
        variant="brand"
        size="xl"
        disabled={isLoading}
        className="mt-4 w-full"
      >
        {isLoading ? 'Wysyłanie...' : submitButtonLabel || 'Wyślij zgłoszenie'}
      </Button>
    </form>
  )
}
